
import { useCallback } from 'react';
import { PopupContent } from '@/types/popup-system';
import { useAuth } from '@/components/AuthProvider';
import { useDatabasePopups } from './popup-manager/useDatabasePopups';
import { useRealtimePopups } from './popup-manager/useRealtimePopups';
import { useScheduledPopups } from './popup-manager/useScheduledPopups';
import { ActivePopup } from './popup-manager/types';

export const usePopupManager = () => {
  let user = null;
  
  // Add error boundary for auth hook
  try {
    const authContext = useAuth();
    user = authContext?.user || null;
  } catch (error) {
    console.warn('Auth hook error in usePopupManager, using fallback:', error);
  }
  
  const {
    activePopups,
    setActivePopups,
    checkDatabasePopups,
    dismissPopupInDatabase,
    storeBroadcastPopup
  } = useDatabasePopups();

  useRealtimePopups({ setActivePopups, checkDatabasePopups });

  const {
    popupQueue,
    addScheduledPopup
  } = useScheduledPopups({ setActivePopups });

  const dismissPopup = useCallback(async (popupId: string) => {
    const dismissedPopup = activePopups.find(p => p.id === popupId);
    
    try {
      setActivePopups(prev => prev.filter(popup => popup.id !== popupId));
      
      // If this is a database popup, mark it as dismissed for this user
      if (dismissedPopup?.dbId && user) {
        await dismissPopupInDatabase(dismissedPopup.dbId, user.id);
      }
    } catch (error) {
      console.error('Error dismissing popup:', error);
    }
  }, [activePopups, user, dismissPopupInDatabase, setActivePopups]);

  const handlePopupAction = useCallback((popupId: string, action: string) => {
    console.log(`Popup ${popupId} action: ${action}`);
    dismissPopup(popupId);
  }, [dismissPopup]);

  const showImmediatePopup = useCallback(async (content: PopupContent, persistForAllUsers?: boolean) => {
    try {
      if (persistForAllUsers) {
        // Store in database for all users to see
        await storeBroadcastPopup(content);
      } else {
        // Show immediately only for current user
        const popup: ActivePopup = {
          ...content,
          persistForAllUsers: false
        };
        setActivePopups(prev => [...prev, popup]);
      }
    } catch (error) {
      console.error('Error showing immediate popup:', error);
    }
  }, [storeBroadcastPopup, setActivePopups]);

  return {
    activePopups,
    dismissPopup,
    handlePopupAction,
    addScheduledPopup,
    showImmediatePopup,
    popupQueue
  };
};
